// Ejecución financiera de un contrato de persona jurídica (C8).
//
// El contrato jurídico NO tiene plan de pagos: la empresa factura cuando emite
// cada factura, sin periodos ni cuotas previstas (daily 2026-08-13). El estado
// financiero por tanto no se compara contra un plan, se DERIVA de las
// solicitudes de pago existentes.
//
// Nada de esto se persiste: es una lectura sobre `paymentRequestsLegal`, igual
// que la advertencia de exceso del formulario de solicitud. Si mañana cambia el
// valor vigente del contrato por una novedad, la ejecución se recalcula sola.

import { PAYMENT_REQUEST_STATUS } from "../../../../constants";
import { isPostApproval } from "./paymentRequestLogic";

export const amountOf = (request) => Number(request?.invoiceAmount) || 0;

// Reparto por estado. Tres bolsas con significados distintos:
//   approved  → plata ya causada contra el contrato: aprobada por el supervisor
//               Y todo el recorrido financiero posterior, incluida la pagada
//               (una factura en Contabilidad o ya pagada no deja de descontar).
//   inReview  → facturada y en manos del supervisor (enviada o en corrección):
//               todavía puede devolverse, pero ya compromete saldo.
//   draft     → borrador de la empresa; NO compromete nada (ni siquiera tiene
//               por qué tener valor). El gestor y el supervisor ni lo ven: el
//               backend filtra los borradores ajenos en GET_PAYMENT_REQUESTS.
// `paid` es un SUBCONJUNTO de approved, para que la vista de facturación
// distinga causado de pagado sin que el total cambie.
export const summarizeExecution = (budget, requests) => {
  const list = Array.isArray(requests) ? requests : [];

  let approved = 0;
  let inReview = 0;
  let draft = 0;
  let paid = 0;
  let approvedCount = 0;
  let inReviewCount = 0;
  let paidCount = 0;

  list.forEach((request) => {
    const amount = amountOf(request);
    const status = request?.status;
    if (isPostApproval(status)) {
      approved += amount;
      approvedCount += 1;
      if (status === PAYMENT_REQUEST_STATUS.PAID) {
        paid += amount;
        paidCount += 1;
      }
      return;
    }
    switch (status) {
      case PAYMENT_REQUEST_STATUS.SUBMITTED:
      case PAYMENT_REQUEST_STATUS.IN_CORRECTION:
        inReview += amount;
        inReviewCount += 1;
        break;
      default:
        draft += amount;
    }
  });

  const total = Number(budget) || 0;
  // Comprometido = lo aprobado más lo que está en trámite. Es contra esto que
  // se mide el saldo: prometerle a la empresa un saldo que ya tiene una factura
  // encima sería invitarla a facturar de más.
  const committed = approved + inReview;
  const balance = total - committed;
  const rate = (value) => (total > 0 ? value / total : 0);

  return {
    budget: total,
    approved,
    inReview,
    draft,
    paid,
    committed,
    balance,
    approvedCount,
    inReviewCount,
    paidCount,
    count: list.length,
    paidRate: rate(paid),
    // % de ejecución financiera: solo lo aprobado. Lo que está en revisión se
    // muestra aparte porque todavía puede devolverse o cambiar de valor.
    executionRate: rate(approved),
    committedRate: rate(committed),
    // Facturado por encima del valor vigente del contrato. Hoy el formulario lo
    // impide (el valor facturado no puede pasarse del saldo, ni al guardar el
    // borrador ni al presentar), pero puede llegarse aquí por otra vía: una
    // novedad que REDUZCA el valor del contrato después de facturar, o
    // solicitudes anteriores a la regla. La vista tiene que poder decirlo.
    overBudget: total > 0 && committed > total,
  };
};
